import { Step3FormProps, WizzardStep3Form } from '../types'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useCallback } from 'react'
import { ActivationObject } from '../../../providers/ActivationObjectProvider/types'
import React from 'react'
import useActivationObject from '../../../providers/ActivationObjectProvider/hooks/useActivationObject'
import { DefaultValue } from '../constants'
import { dateTimeToApiString } from '../../../../utils/dateTimeUtils'
import { VALIDATION_REQUIRED } from '../../../../constants/validation'

const useStep3Form = (activationObject?: ActivationObject): Step3FormProps => {
    const propName = (name: keyof WizzardStep3Form): string => name

    const validDate = yup.date().required(VALIDATION_REQUIRED).typeError('Neplatný formát')

    const formValidationSchema = yup.object().shape({
        [propName('locationDog')]: yup.string(),
        [propName('handoverKeys')]: yup.string().required(VALIDATION_REQUIRED),
        [propName('setControlChips')]: yup.boolean(),
        [propName('customerPresence')]: yup.boolean(),
        [propName('controlType')]: yup.string(),
        [propName('handoverDate')]: yup
            .date()
            .nullable()
            .when('handoverKeys', {
                is: 'LandAndObject',
                then: validDate,
            })
            .when('handoverKeys', {
                is: 'OnlyLand',
                then: validDate,
            })
            .when('handoverKeys', {
                is: 'NotHandoverKeys',
                then: yup.date().when('customerPresence', {
                    is: true,
                    then: validDate,
                }),
            }),
        [propName('handoverHour')]: yup
            .number()
            .when('handoverKeys', {
                is: 'LandAndObject',
                then: yup.number().required(VALIDATION_REQUIRED),
            })
            .when('handoverKeys', {
                is: 'OnlyLand',
                then: yup.number().required(VALIDATION_REQUIRED),
            })
            .when('handoverKeys', {
                is: 'NotHandoverKeys',
                then: yup.number().when('customerPresence', {
                    is: true,
                    then: yup.number().required(VALIDATION_REQUIRED),
                }),
            }),
    })
    const form = useForm<WizzardStep3Form>({
        criteriaMode: 'all',
        reValidateMode: 'onChange',
        resolver: yupResolver(formValidationSchema),
        defaultValues: DefaultValue,
    })
    const reset = form.reset
    React.useEffect(() => {
        if (activationObject) {
            reset({
                locationDog: !!activationObject?.locationDog ? activationObject?.locationDog : DefaultValue.locationDog,
                handoverKeys: !!activationObject?.handoverKeys ? activationObject?.handoverKeys : DefaultValue.handoverKeys,
                setControlChips: activationObject?.setControlChips ?? DefaultValue.setControlChips,
                customerPresence: activationObject?.customerPresence ?? DefaultValue.customerPresence,
                controlType: activationObject?.controlType ?? DefaultValue.controlType,
                handoverDate:
                    activationObject && activationObject.handoverKeysDateTime
                        ? new Date(activationObject.handoverKeysDateTime)
                        : DefaultValue.handoverDate,
                handoverHour: activationObject.handoverKeysDateTime
                    ? new Date(activationObject.handoverKeysDateTime).getHours()
                    : DefaultValue.handoverHour,
            })
        }
    }, [activationObject, reset])

    const { update } = useActivationObject()
    const onSubmit = useCallback(
        (data: WizzardStep3Form) => {
            if (activationObject) {
                const handoverKeysDateTime = data.handoverDate
                if (handoverKeysDateTime) {
                    handoverKeysDateTime.setHours(data.handoverHour || 0)
                    handoverKeysDateTime.setMinutes(0)
                }
                const newObject: ActivationObject = {
                    ...activationObject,
                    ...data,
                    handoverKeysDateTime: handoverKeysDateTime ? dateTimeToApiString(handoverKeysDateTime) : null,
                    wizardStep: 'OperationGuidelines',
                }
                update(newObject)
            }
        },
        [activationObject, update]
    )
    return {
        form,
        onSubmit,
    }
}

export default useStep3Form
